import { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Pencil, Trash2, TrendingUp, X } from "lucide-react";
import {
  listClients, createClient, updateClient, deleteClient,
  listPipeline, createPipeline, deletePipeline,
} from "@/api/entities";
import { Panel, Label, Heading, Loading, Pill, HudButton, Empty, accent } from "@/components/hud";
import { useOperator } from "@/context/OperatorContext";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

const STATUS_TONE = { active: "mint", prospect: "amber", paused: "violet", churned: "magenta" };

export default function Clients() {
  const { canWrite } = useOperator();
  const [clients, setClients] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // client object or {} for new
  const [pipeForm, setPipeForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([listClients(), listPipeline()]);
      setClients(c.items || c || []);
      setPipeline(p.items || p || []);
    } catch {
      toast.error("Could not load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveClient = async (form) => {
    try {
      if (form.id) await updateClient(form.id, form);
      else await createClient(form);
      toast.success(form.id ? "Client updated" : "Client added");
      setEditing(null);
      load();
    } catch {
      toast.error("Save needs operator sign-in");
    }
  };

  const removeClient = async (id) => {
    try {
      await deleteClient(id);
      toast.success("Client removed");
      load();
    } catch {
      toast.error("Delete needs operator sign-in");
    }
  };

  const savePipe = async (form) => {
    try {
      await createPipeline(form);
      toast.success("Opportunity added to pipeline");
      setPipeForm(null);
      load();
    } catch {
      toast.error("Save needs operator sign-in");
    }
  };

  const removePipe = async (id) => {
    try {
      await deletePipeline(id);
      toast.success("Opportunity removed");
      load();
    } catch {
      toast.error("Delete needs operator sign-in");
    }
  };

  if (loading) return <Loading label="Loading client roster…" />;

  return (
    <div className="space-y-6 yb-rise" data-testid="clients-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Label>Clients · Melbourne SMB</Label>
          <h1 className="font-display text-[26px] sm:text-[32px] mt-1.5" style={{ color: "var(--yb-text)" }}>
            Client Roster
          </h1>
        </div>
        {canWrite && (
          <HudButton onClick={() => setEditing({})}><Plus size={15} /> Add client</HudButton>
        )}
      </div>

      <Panel>
        {clients.length === 0 ? (
          <Empty icon={Building2} title="No clients yet"
            hint="Add your first Melbourne SMB client to start tracking engagements."
            action={canWrite && <HudButton onClick={() => setEditing({})}><Plus size={15} /> Add client</HudButton>} />
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[13px]" style={{ minWidth: 640 }}>
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-wider text-left" style={{ color: "var(--yb-text-muted)" }}>
                  <th className="py-2 px-2 font-normal">Client</th>
                  <th className="py-2 px-2 font-normal">Industry</th>
                  <th className="py-2 px-2 font-normal">Status</th>
                  <th className="py-2 px-2 font-normal text-right">MRR</th>
                  {canWrite && <th className="py-2 px-2 font-normal text-right">·</th>}
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--yb-hairline)" }}>
                    <td className="py-3 px-2">
                      <div style={{ color: "var(--yb-text)" }}>{c.name}</div>
                      <div className="font-mono text-[11px]" style={{ color: "var(--yb-text-muted)" }}>{c.contact || "—"}</div>
                    </td>
                    <td className="py-3 px-2" style={{ color: "var(--yb-text-dim)" }}>{c.industry || "—"}</td>
                    <td className="py-3 px-2">
                      <span className="pill" style={{ color: accent(STATUS_TONE[c.status] || "cyan").c }}>{c.status || "prospect"}</span>
                    </td>
                    <td className="py-3 px-2 font-mono text-right" style={{ color: "var(--yb-mint)" }}>
                      {fmtMoney(c.mrr_cents || 0)}
                    </td>
                    {canWrite && (
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1 justify-end">
                          <IconBtn onClick={() => setEditing(c)} label="Edit client"><Pencil size={14} /></IconBtn>
                          <IconBtn onClick={() => removeClient(c.id)} label="Delete client" tone="magenta"><Trash2 size={14} /></IconBtn>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* pipeline — kept visually distinct from settled money */}
      <Panel style={{ borderColor: "var(--yb-amber)" }}>
        <Heading title="Pipeline (projected)" kicker="forecast · not revenue" accent="amber"
          right={canWrite && <HudButton variant="ghost" tone="amber" onClick={() => setPipeForm({})}><Plus size={14} /> Add opportunity</HudButton>} />
        {pipeline.length === 0 ? (
          <Empty icon={TrendingUp} title="Pipeline empty" hint="Track open opportunities here. These are projections and never count as settled revenue." />
        ) : (
          <ul className="space-y-2">
            {pipeline.map((p) => (
              <li key={p.id} className="flex items-center gap-3 p-3 rounded"
                  style={{ border: "1px solid var(--yb-hairline)", background: "var(--yb-panel-2)" }}>
                <TrendingUp size={15} style={{ color: "var(--yb-amber)" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] truncate" style={{ color: "var(--yb-text)" }}>{p.title || p.name}</div>
                  <div className="font-mono text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
                    {p.client_name || "—"} · {p.stage || "open"} · {p.probability != null ? `${p.probability}% likely` : "unweighted"}
                  </div>
                </div>
                <span className="font-mono text-[14px]" style={{ color: "var(--yb-amber)" }}>{fmtMoney(p.value_cents || 0)}</span>
                {canWrite && <IconBtn onClick={() => removePipe(p.id)} label="Remove opportunity" tone="magenta"><Trash2 size={14} /></IconBtn>}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {editing && <ClientModal initial={editing} onSave={saveClient} onClose={() => setEditing(null)} />}
      {pipeForm && <PipelineModal onSave={savePipe} onClose={() => setPipeForm(null)} />}
    </div>
  );
}

function IconBtn({ children, onClick, label, tone = "cyan" }) {
  const { c } = accent(tone);
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5"
      style={{ border: "1px solid var(--yb-hairline)", color: c }}>
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full h-10 px-3 rounded text-[13px] bg-transparent outline-none";
const inputStyle = { border: "1px solid var(--yb-hairline)", color: "var(--yb-text)" };

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(3,6,15,0.78)" }} onClick={onClose}>
      <div className="hud-panel w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="flex items-center justify-between mb-4">
          <span className="font-display text-[16px]" style={{ color: "var(--yb-cyan)" }}>{title}</span>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5"
                  style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-muted)" }}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ClientModal({ initial, onSave, onClose }) {
  const [f, setF] = useState({
    id: initial.id, name: initial.name || "", contact: initial.contact || "",
    industry: initial.industry || "", status: initial.status || "prospect",
    mrr: ((initial.mrr_cents || 0) / 100).toString(),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      id: f.id, name: f.name.trim(), contact: f.contact.trim(), industry: f.industry.trim(),
      status: f.status, mrr_cents: Math.round(parseFloat(f.mrr || "0") * 100) || 0,
    });
  };
  return (
    <ModalShell title={initial.id ? "Edit client" : "Add client"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Business name"><input className={inputCls} style={inputStyle} value={f.name} onChange={set("name")} required autoFocus /></Field>
        <Field label="Contact"><input className={inputCls} style={inputStyle} value={f.contact} onChange={set("contact")} placeholder="name · email" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Industry"><input className={inputCls} style={inputStyle} value={f.industry} onChange={set("industry")} /></Field>
          <Field label="Status">
            <select className={inputCls} style={inputStyle} value={f.status} onChange={set("status")}>
              <option value="prospect">prospect</option>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="churned">churned</option>
            </select>
          </Field>
        </div>
        <Field label="MRR (AUD)"><input className={inputCls} style={inputStyle} type="number" step="0.01" min="0" value={f.mrr} onChange={set("mrr")} /></Field>
        <div className="flex gap-2 pt-2">
          <HudButton type="submit" disabled={!f.name.trim()}>Save</HudButton>
          <HudButton type="button" variant="ghost" onClick={onClose}>Cancel</HudButton>
        </div>
      </form>
    </ModalShell>
  );
}

function PipelineModal({ onSave, onClose }) {
  const [f, setF] = useState({ title: "", client_name: "", stage: "qualified", value: "", probability: "50" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      title: f.title.trim(), client_name: f.client_name.trim(), stage: f.stage,
      value_cents: Math.round(parseFloat(f.value || "0") * 100) || 0,
      probability: parseInt(f.probability, 10) || 0,
    });
  };
  return (
    <ModalShell title="Add opportunity" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Opportunity"><input className={inputCls} style={inputStyle} value={f.title} onChange={set("title")} required autoFocus /></Field>
        <Field label="Client"><input className={inputCls} style={inputStyle} value={f.client_name} onChange={set("client_name")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stage">
            <select className={inputCls} style={inputStyle} value={f.stage} onChange={set("stage")}>
              <option value="lead">lead</option>
              <option value="qualified">qualified</option>
              <option value="proposal">proposal</option>
              <option value="negotiation">negotiation</option>
            </select>
          </Field>
          <Field label="Probability %"><input className={inputCls} style={inputStyle} type="number" min="0" max="100" value={f.probability} onChange={set("probability")} /></Field>
        </div>
        <Field label="Value (AUD)"><input className={inputCls} style={inputStyle} type="number" step="0.01" min="0" value={f.value} onChange={set("value")} /></Field>
        <div className="flex gap-2 pt-2">
          <HudButton type="submit" tone="amber" disabled={!f.title.trim()}>Add</HudButton>
          <HudButton type="button" variant="ghost" onClick={onClose}>Cancel</HudButton>
        </div>
      </form>
    </ModalShell>
  );
}
